/**
 * Text input primitive.
 *
 * `dir` is intentionally **not** forced: a Persian label keeps the natural right-to-left flow, while identifiers
 * and addresses opt into `dir="ltr"` at the call site through {@link LtrInput}.
 */

import { forwardRef } from 'react';

import { cn } from '@/lib/utils';

/** Accessible text input with the design system's states. */
export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type = 'text', ...props }, ref) => (
    <input
      ref={ref}
      type={type}
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive',
        className,
      )}
      {...props}
    />
  ),
);

Input.displayName = 'Input';

/**
 * Input for values that are always Latin — e-mail addresses, API keys, model identifiers, URLs.
 *
 * Forcing `dir="ltr"` here (rather than at every call site) is what keeps a pasted key from rendering with its
 * punctuation reordered, which is a real and confusing failure mode in a right-to-left layout.
 */
export const LtrInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <Input ref={ref} dir="ltr" className={cn('text-start', className)} {...props} />
  ),
);

LtrInput.displayName = 'LtrInput';

/**
 * Multi-line input, used by the wizard's free-text answers and the co-pilot's question box.
 *
 * The direction is not forced: a Persian answer must keep the right-to-left flow, while the prompt preview next to it
 * opts into `dir="auto"` because a prompt may be written in either language.
 */
export const Textarea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, rows = 4, ...props }, ref) => (
  <textarea
    ref={ref}
    rows={rows}
    className={cn(
      'flex w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive',
      className,
    )}
    {...props}
  />
));

Textarea.displayName = 'Textarea';
